import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { PublicWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import { LuFileText, LuRefreshCw, LuFileCheck, LuZap, LuCheck } from 'react-icons/lu';
import '../../../styles/wizard.css';
import { INTENT_TYPES } from '../../../Config/Onboarding/constant';
import { isWizardCompletedUser } from '../../../utils/authRedirectUtils';

const intentOptions = [
  {
    type: INTENT_TYPES.MANUAL_TO_AUTOMATION,
    icon: LuFileText,
    title: 'Convert Manual Tests to Automation',
    description: 'Upload Excel/CSV or paste cases; we\'ll convert them into automation-ready flows.',
    disabled: true,
    badge: 'Coming Soon'
  },
  {
    type: INTENT_TYPES.MIGRATE_SCRIPTS,
    icon: LuRefreshCw,
    title: 'Migrate Existing Automation Scripts',
    description: 'Import your Selenium/Cypress/Playwright/Robot code and standardize it in Robitest.',
    disabled: true,
    badge: 'Coming Soon'
  },
  {
    type: INTENT_TYPES.JIRA_REQUIREMENTS,
    icon: LuFileCheck,
    title: 'Generate Tests from Requirements',
    description: 'Connect Project Management tool or upload requirements; we\'ll generate scenarios and suites.',
    disabled: false
  },
  {
    type: INTENT_TYPES.SAMPLE_PROJECT,
    icon: LuZap,
    title: 'Explore with Sample Project',
    description: 'Try Robitest instantly with a pre-configured demo project.',
    disabled: true,
    badge: 'Coming Soon'
  }
];

const Step1Intent = () => {
  const navigate = useNavigate();
  const { initializeStepFlow, nextStep, state } = useOnboarding();
  const [selectedIntent, setSelectedIntent] = useState(state.engine.intent);
  const isWizardCompleted = isWizardCompletedUser();
  const hasSkippedIntentRef = useRef(false);

  // Users who already completed the wizard can't revisit intent selection - move them on to Step 2
  useEffect(() => {
    if (!isWizardCompleted || hasSkippedIntentRef.current) return;
    hasSkippedIntentRef.current = true;

    initializeStepFlow(
      state.engine.intent || localStorage.getItem('wizard_intent') || INTENT_TYPES.JIRA_REQUIREMENTS
    );
    nextStep();
    navigate('/app/onboarding?step=2', { replace: true });
  }, [isWizardCompleted, state.engine.intent, initializeStepFlow, nextStep, navigate]);

  // useEffect(() => {
  //   // Clear old wizard data when landing on intent selection
  //   const draft = localStorage.getItem('robitest_onboarding_draft_v1');
  //   if (draft) {
  //     try {
  //       const parsed = JSON.parse(draft);
  //       // Reset to step 1 if we're starting fresh
  //       if (!parsed.engine?.intent) {
  //         parsed.engine = { intent: null, flow: { stepOrder: [], currentStep: 1, completedSteps: [] }, steps: {} };
  //         localStorage.setItem('robitest_onboarding_draft_v1', JSON.stringify(parsed));
  //       }
  //     } catch (e) {
  //       localStorage.removeItem('robitest_onboarding_draft_v1');
  //     }
  //   }
  // }, []);

  const handleIntentSelect = (intentType) => {
    if (!intentOptions.find(opt => opt.type === intentType)?.disabled) {
      setSelectedIntent((prev) => (prev === intentType ? null : intentType));
    }
  };

  const handleContinue = () => {
    if (!selectedIntent) return;
    
    // Initialize step flow based on selected intent
    initializeStepFlow(selectedIntent);
    
    // Store intent for use in login API
    localStorage.setItem('wizard_intent', selectedIntent);
    
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    
    if (isAuthenticated) {
      nextStep();
      navigate('/app/onboarding?step=2');
    } else {
      // Not logged in, show AuthGate within wizard (embedded auth experience)
      navigate('/get-started/auth');
    }
  };

  const orderedOptions = [
    ...intentOptions.filter((o) => !o.disabled),
    ...intentOptions.filter((o) => o.disabled),
  ];

  if (isWizardCompleted) return null;

  return (
    <PublicWizardLayout>
      <WizardContainer>
        <div className="ob-hero">
          <p className="ob-hero__eyebrow">Welcome to Robitest</p>
          <h1 className="ob-hero__title">Let&apos;s get your first automation running</h1>
          <p className="ob-hero__subtitle">
            Choose a starting path and we&apos;ll guide you through setting up your project, connecting your
            sources, creating tests and running them.
          </p>
          <ol className="ob-hero__journey" aria-label="What happens next">
            <li className="is-active"><span>1</span> Project</li>
            <li><span>2</span> Connect</li>
            <li><span>3</span> Create</li>
            <li><span>4</span> Run</li>
          </ol>
        </div>
        
        <WizardCard
          title="How do you want to start?"
          subtitle="Pick the option that best matches your workflow. You can add more sources later."
        >
          <div className="ob-choice-grid ob-choice-grid--2" role="group" aria-label="Starting options">
            {orderedOptions.map((option) => {
              const IconComponent = option.icon;
              const isDisabled = option.disabled;
              const isSelected = selectedIntent === option.type && !isDisabled;
              
              return (
                <button
                  type="button"
                  key={option.type}
                  className={`ob-choice ${isSelected ? 'is-selected' : ''} ${isDisabled ? 'is-disabled' : ''}`}
                  onClick={() => handleIntentSelect(option.type)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  {isSelected && (
                    <span className="ob-choice__check" aria-hidden="true">
                      <LuCheck size={13} strokeWidth={3} />
                    </span>
                  )}
                  {isDisabled && option.badge && (
                    <span className="ob-choice__badges">
                      <span className="ob-badge">{option.badge}</span>
                    </span>
                  )}
                  <span className="ob-choice__icon" aria-hidden="true">
                    <IconComponent size={20} strokeWidth={1.9} />
                  </span>
                  <span className="ob-choice__body">
                    <span className="ob-choice__title">{option.title}</span>
                    <span className="ob-choice__desc">{option.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <WizardActions
            showBack={false}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={!selectedIntent}
          />
        </WizardCard>
      </WizardContainer>
    </PublicWizardLayout>
  );
};

export default Step1Intent;